import React, { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';

const PaymentSuccess = () => {
    const location = useLocation();
    const { transactionId, amount, items } = location.state || {};
    const [purchaseData, setPurchaseData] = useState(null);
    const [fileData, setFileData] = useState({});
    const [loading, setLoading] = useState({});
    const [isMobile, setIsMobile] = useState(false);

    // Detect mobile device on component mount
    useEffect(() => {
        const checkMobile = () => {
            return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        };
        setIsMobile(checkMobile());
    }, []);

    useEffect(() => {
        // Get purchase data from localStorage
        const currentOrderStr = localStorage.getItem('current_order');
        if (currentOrderStr) {
            try {
                const orderData = JSON.parse(currentOrderStr);
                setPurchaseData(orderData);
                console.log('Purchase data loaded:', orderData);
            } catch (error) {
                console.error('Error parsing purchase data:', error);
                toast.error('Failed to load purchase data');
            }
        }
        
        // Clear cart
        const examType = window.location.pathname.split('/')[2];
        localStorage.removeItem(`cart_${examType}`);
    }, []);

    // Mobile-optimized download function
    const handleMobileDownload = async (itemId, type, itemName) => {
        const key = `${itemId}-${type}-download`;
        
        if (loading[key]) return;
        
        try {
            setLoading(prev => ({ ...prev, [key]: true }));
            
            if (!purchaseData) {
                toast.error('No purchase information found.');
                return;
            }
            
            const userEmail = purchaseData.userEmail;
            const paymentReference = purchaseData.orderId || transactionId;
            
            if (!userEmail || !paymentReference) {
                toast.error('Missing purchase information.');
                return;
            }
            
            // Build download URL
            const downloadUrl = `https://pastq.eddcodesgh.com/qApi/client/downloads/download.php`;
            const params = new URLSearchParams({
                itemId: itemId,
                type: type,
                email: userEmail,
                reference: paymentReference,
                action: isMobile ? 'view' : 'download' // Mobile gets view, desktop gets download
            });
            
            const fullUrl = `${downloadUrl}?${params.toString()}`;
            console.log('Download URL:', fullUrl, 'Mobile:', isMobile);
            
            // Different strategies for mobile vs desktop
            if (isMobile) {
                // MOBILE STRATEGY 1: Use fetch API with Blob (most reliable for mobile)
                toast.info('Preparing download for mobile...');
                
                try {
                    const response = await fetch(fullUrl, {
                        method: 'GET',
                        credentials: 'include'
                    });
                    
                    if (response.ok) {
                        const blob = await response.blob();
                        
                        // Create object URL
                        const url = window.URL.createObjectURL(blob);
                        
                        // Create download link
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = `${itemName}_${type}.pdf`;
                        link.style.display = 'none';
                        
                        // Append and trigger download
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        
                        // Clean up
                        setTimeout(() => {
                            window.URL.revokeObjectURL(url);
                        }, 1000);
                        
                        toast.success('Download started! Check your downloads folder.');
                    } else {
                        // Fallback to direct navigation
                        throw new Error('Fetch failed');
                    }
                } catch (fetchError) {
                    console.log('Fetch method failed, trying direct navigation:', fetchError);
                    
                    // MOBILE STRATEGY 2: Direct navigation (works on most mobile browsers)
                    toast.info('Opening PDF in browser...');
                    
                    // Create an iframe as backup
                    const iframe = document.createElement('iframe');
                    iframe.style.display = 'none';
                    iframe.src = fullUrl;
                    document.body.appendChild(iframe);
                    
                    // Also try direct navigation
                    setTimeout(() => {
                        window.location.href = fullUrl;
                    }, 300);
                    
                    // Remove iframe after some time
                    setTimeout(() => {
                        if (document.body.contains(iframe)) {
                            document.body.removeChild(iframe);
                        }
                    }, 5000);
                }
            } else {
                // DESKTOP STRATEGY: Traditional download
                const link = document.createElement('a');
                link.href = fullUrl;
                link.download = `${itemName}_${type}.pdf`;
                link.target = '_blank';
                link.rel = 'noopener noreferrer';
                
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                
                toast.success('Download started!');
            }
            
            // Log successful access
            setFileData(prev => ({
                ...prev,
                [`${itemId}-${type}`]: { accessed: true, device: isMobile ? 'mobile' : 'desktop' }
            }));
            
        } catch (error) {
            console.error('Download error:', error);
            
            // Ultimate fallback for all devices
            if (purchaseData) {
                const fallbackUrl = `https://pastq.eddcodesgh.com/qApi/client/downloads/download.php?itemId=${itemId}&type=${type}&email=${encodeURIComponent(purchaseData.userEmail)}&reference=${purchaseData.orderId}&action=view`;
                window.open(fallbackUrl, '_blank');
                toast.info('Opening file in new tab...');
            }
        } finally {
            setLoading(prev => ({ ...prev, [key]: false }));
        }
    };

    // View PDF function (opens in new tab)
    const handleViewPdf = (itemId, type) => {
        if (!purchaseData) {
            toast.error('No purchase information found.');
            return;
        }
        
        const userEmail = purchaseData.userEmail;
        const paymentReference = purchaseData.orderId || transactionId;
        
        if (!userEmail || !paymentReference) {
            toast.error('Missing purchase information.');
            return;
        }
        
        const viewUrl = `https://pastq.eddcodesgh.com/qApi/client/downloads/download.php?itemId=${itemId}&type=${type}&email=${encodeURIComponent(userEmail)}&reference=${encodeURIComponent(paymentReference)}&action=view`;
        
        const newTab = window.open(viewUrl, '_blank');
        if (!newTab) {
            toast.warning('Please allow pop-ups to view PDF');
            // Fallback to download
            handleMobileDownload(itemId, type, `${items?.find(item => item.id === itemId)?.subject || 'file'}`);
        } else {
            toast.success('Opening PDF in new tab...');
        }
    };

    // Verify all items on component mount
    useEffect(() => {
        if (purchaseData && items?.length > 0) {
            // Pre-verify each item
            items.forEach(item => {
                const key = `${item.id}-${item.type}`;
                if (!fileData[key]) {
                    setFileData(prev => ({
                        ...prev,
                        [key]: { verified: true, accessed: false }
                    }));
                }
            });
        }
    }, [purchaseData, items]);

    // Show mobile-specific instructions
    useEffect(() => {
        if (isMobile && items?.length > 0) {
            console.log('Mobile device detected, showing mobile instructions');
        }
    }, [isMobile, items]);

    return (
        <div className="container py-5">
            <div className="row justify-content-center">
                <div className="col-lg-8">
                    <div className="card shadow-lg border-0">
                        <div className="card-body p-5 text-center">
                            <div className="text-success mb-4">
                                <i className="bi bi-check-circle-fill display-1"></i>
                            </div>
                            <h2 className="fw-bold mb-3">Payment Successful!</h2>
                            <p className="lead mb-4">
                                Thank you for your purchase. Your files are ready to access.
                            </p>
                            
                            {/* Mobile-specific warning */}
                            {isMobile && (
                                <div className="alert alert-info mb-4 text-start">
                                    <i className="bi bi-phone me-2"></i>
                                    <strong>Mobile Device Detected:</strong> For best results, 
                                    use the "View" option first, then download from your browser.
                                </div>
                            )}
                            
                            {/* Transaction Details */}
                            <div className="card bg-light mb-4">
                                <div className="card-body text-start">
                                    <h5 className="card-title mb-3">Transaction Details</h5>
                                    <div className="row">
                                        <div className="col-md-6">
                                            <p><strong>Order ID:</strong><br/>{purchaseData?.orderId || 'N/A'}</p>
                                            <p><strong>Amount Paid:</strong><br/>GHS {amount || purchaseData?.amount || '0.00'}</p>
                                        </div>
                                        <div className="col-md-6">
                                            <p><strong>Items Purchased:</strong><br/>{items?.length || 0} items</p>
                                            <p><strong>Customer Email:</strong><br/>{purchaseData?.userEmail || 'N/A'}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Purchased Items */}
                            <div className="mb-5">
                                <h5 className="mb-3">Your Purchased Items</h5>
                                <div className="list-group">
                                    {items?.map((item, index) => {
                                        const key = `${item.id}-${item.type}`;
                                        const fileInfo = fileData[key];
                                        const isLoadingDownload = loading[`${item.id}-${item.type}-download`];
                                        const isLoadingView = loading[`${item.id}-${item.type}-view`];
                                        const isVerified = fileInfo?.verified;
                                        const itemName = `${item.subject}_${item.year}`;
                                        
                                        return (
                                            <div key={index} className="list-group-item">
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div>
                                                        <h6 className="mb-1">{item.subject} - {item.year}</h6>
                                                        <small className="text-muted">
                                                            {item.type === 'question' ? 'Question Paper' : 'Answer Sheet'}
                                                        </small>
                                                        <div className="mt-1">
                                                            <small className="badge bg-success">
                                                                ✓ Purchased
                                                            </small>
                                                            {fileInfo?.accessed && (
                                                                <small className={`badge ms-1 ${fileInfo.device === 'mobile' ? 'bg-info' : 'bg-primary'}`}>
                                                                    {fileInfo.device === 'mobile' ? '📱 Accessed' : '💻 Accessed'}
                                                                </small>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div className="btn-group" role="group">
                                                            <button
                                                                className="btn btn-sm btn-primary"
                                                                onClick={() => handleViewPdf(item.id, item.type)}
                                                                disabled={isLoadingView}
                                                                title="View PDF in browser"
                                                            >
                                                                {isLoadingView ? (
                                                                    <span className="spinner-border spinner-border-sm me-1"></span>
                                                                ) : (
                                                                    <i className="bi bi-eye me-1"></i>
                                                                )}
                                                                View
                                                            </button>
                                                            <button
                                                                className="btn btn-sm btn-success"
                                                                onClick={() => handleMobileDownload(item.id, item.type, itemName)}
                                                                disabled={isLoadingDownload}
                                                                title="Download PDF"
                                                            >
                                                                {isLoadingDownload ? (
                                                                    <span className="spinner-border spinner-border-sm me-1"></span>
                                                                ) : (
                                                                    <>
                                                                        <i className="bi bi-download me-1"></i>
                                                                        {isMobile ? 'Save' : 'Download'}
                                                                    </>
                                                                )}
                                                            </button>
                                                        </div>
                                                        <span className="badge bg-success ms-2">GHS {item.price || '0.00'}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Device-specific instructions */}
                            <div className="mt-4">
                                {isMobile ? (
                                    <div className="alert alert-warning text-start">
                                        <h6><i className="bi bi-info-circle me-2"></i>Mobile Download Tips:</h6>
                                        <ul className="mb-0">
                                            <li>Use <strong>"View"</strong> to open PDF in browser first</li>
                                            <li>Then tap the <strong>share icon</strong> in your browser</li>
                                            <li>Select <strong>"Save to Files"</strong> or <strong>"Download"</strong></li>
                                            <li>For large files, use Wi-Fi connection</li>
                                        </ul>
                                    </div>
                                ) : (
                                    <div className="text-start text-muted">
                                        <small>
                                            <strong>Note:</strong> If downloads don't start automatically, 
                                            check your browser's pop-up blocker or try the "View" option.
                                        </small>
                                    </div>
                                )}
                            </div>

                            <div className="d-grid gap-2 d-md-flex justify-content-md-center mt-4">
                                <Link to="/questions" className="btn btn-outline-primary">
                                    <i className="bi bi-arrow-left me-2"></i>
                                    Back to Questions
                                </Link>
                                <Link to="/dashboard" className="btn btn-primary">
                                    <i className="bi bi-house me-2"></i>
                                    Go to Dashboard
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PaymentSuccess;